import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useMemo, useCallback, useState, Suspense } from "react";
import { useAuthContext } from "@/hooks/AuthProvider";
import { useEtablissement } from "@/hooks/useEtablissements";
import { useInactivityTimeout } from "@/hooks/useInactivityTimeout";
import { useHasAcceptedCgu } from "@/hooks/useCguAcceptance";
import { Header } from "@/components/Header";
import { MobileHeader } from "@/components/layouts/MobileHeader";
import { BottomNav } from "@/components/layouts/BottomNav";
import { SidebarAdmin } from "@/components/SidebarAdmin";
import { SidebarUser } from "@/components/SidebarUser";
import { NavigationContext, resolveRoute } from "@/hooks/useAppNavigation";
import { DemandeClientMemoProvider } from "@/hooks/useDemandeClientMemo";
import { lazyNamed } from "@/lib/lazyNamed";
import { useIsMobile } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";
import { displayName } from "@/lib/displayName";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Lazy : PanneauDemandeClient importe motion/react et n'est rendu qu'en admin desktop
// (cf. <main> plus bas). Le charger a la demande le sort du bundle d'entree charge
// par 100% des sessions.
const PanneauDemandeClient = lazyNamed(
  () => import("@/components/PanneauDemandeClient"),
  "PanneauDemandeClient",
);

// Mapping inverse : route -> nom de page (pour currentPage du sidebar)
const reverseRouteMap: Record<string, string> = {
  "/": "dashboard",
  "/chantiers": "gestion-operationnelle-user",
  "/chantiers/nouveau": "nouvelle-demande-chantier",
  "/transports": "mes-transports-user",
  "/transports/nouveau": "nouvelle-demande-transport",
  "/cap": "mes-cap-user",
  "/operations": "gestion-operations",
  "/prix": "prix-gestion",
  "/types-categories": "types-categories-admin",
  "/documents-admin": "documents-administratifs-admin",
  "/supervision": "supervision",
  "/documents": "documents-administratifs",
  "/demandes": "mes-demandes",
  "/mes-entreprises": "nouveau-site",
  "/etablissements": "admin-client",
  "/mon-etablissement": "gestion-etablissement",
  "/profil": "profile",
};

// Routes dont l'ID sidebar depend du role
const roleRouteMap: Record<string, { admin: string; user: string }> = {
  "/devis-factures": { admin: "devis-factures-admin", user: "devis-factures" },
};

// Routes plein ecran de formulaire (wizards) : la BottomNav est masquee car ces
// ecrans portent leur propre navigation + barre d'action safe-area-aware.
// Couvre /chantiers|/transports + nouveau|devis|commande|dechet/nouveau (avec ou sans /:id/).
const FULLSCREEN_FORM_ROUTE =
  /^\/(chantiers|transports)\/([^/]+\/)?(nouveau|devis|commande|dechet\/nouveau)$/;

export function AuthLayout() {
  const { user, role, appRole, userData, etablissementId, aal, factors, loading, signOut } = useAuthContext();
  const cguQuery = useHasAcceptedCgu(user?.id);
  const { data: etablissement } = useEtablissement(etablissementId ?? undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isSigningOut, setIsSigningOut] = useState(false);


  const handleNavigate = useCallback((page: string, id?: string) => {
    navigate(resolveRoute(page, id));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      toast.info("Vous avez été deconnecte");
      navigate("/login");
      // Pas de reset isSigningOut : la garde "if (!user) <Navigate to=/login>"
      // demonte AuthLayout, le state est jete avec.
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur lors de la deconnexion";
      toast.error(msg);
      setIsSigningOut(false);
    }
  }, [signOut, navigate]);

  const handleInactivityLogout = useCallback(async () => {
    await signOut();
    toast.warning("Deconnecte apres 3 heures d'inactivite");
    navigate("/login");
  }, [signOut, navigate]);

  // Desactive en MODE === 'development' (Vite dev server) ; actif en preview/prod/tests.
  useInactivityTimeout(handleInactivityLogout, {
    disabled: !user || import.meta.env.MODE === "development",
  });

  const navContext = useMemo(
    () => ({ onNavigate: handleNavigate, onBack: handleBack }),
    [handleNavigate, handleBack]
  );

  // Tous les hooks sont au-dessus — les returns conditionnels sont en dessous
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Chargement...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // MFA obligatoire pour admin_serme. Important : ne pas decider tant que l'etat MFA
  // (factors + aal) n'est pas charge, sinon on redirige par erreur vers /mfa-enroll
  // alors qu'un factor verified existe deja.
  if (appRole === 'admin_serme') {
    if (aal === null) {
      return <div className="flex items-center justify-center h-screen">Chargement...</div>;
    }
    const hasVerifiedFactor = factors.some((f) => f.status === 'verified');
    if (!hasVerifiedFactor) {
      return <Navigate to="/mfa-enroll" replace />;
    }
    if (aal.current === 'aal1' && aal.next === 'aal2') {
      return <Navigate to="/mfa-challenge" state={{ from: location.pathname }} replace />;
    }
  }

  // Gate CGU (RGPD-R01, PRODUIT/domaine/19_donnees_personnelles.md) : tous les utilisateurs
  // connectes, independant du MFA.
  if (cguQuery.isPending) {
    return <div className="flex items-center justify-center h-screen">Chargement...</div>;
  }
  // Erreur de lecture (hoquet reseau/RLS) : NE PAS rediriger vers l'acceptation, sinon un
  // utilisateur ayant deja accepte serait renvoye re-accepter. On propose un retry.
  if (cguQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3">
        <p className="text-sm text-muted-foreground">Erreur de chargement. Veuillez réessayer.</p>
        <Button variant="outline" onClick={() => cguQuery.refetch()}>Réessayer</Button>
      </div>
    );
  }
  // data === false : acceptation requise. (data === true : on passe.)
  if (cguQuery.data !== true) {
    return <Navigate to="/cgu-acceptation" state={{ from: location.pathname }} replace />;
  }

  const userRole = role as "admin" | "user";
  const roleKey = roleRouteMap[location.pathname];
  const currentPage = roleKey
    ? roleKey[userRole]
    : reverseRouteMap[location.pathname] || "";

  const isFullscreenForm = FULLSCREEN_FORM_ROUTE.test(location.pathname);

  const userName = userData ? `${userData.prenom} ${userData.nom}` : undefined;
  const etablissementNom = etablissement ? displayName(etablissement) : undefined;

  return (
    <NavigationContext.Provider value={navContext}>
      <DemandeClientMemoProvider>
        <div className="min-h-screen flex flex-col">
          {isMobile ? (
            <MobileHeader
              onLogout={handleLogout}
              isSigningOut={isSigningOut}
              onNavigateToProfile={() => navigate("/profil")}
              userRole={userRole}
              userName={userName}
              etablissementNom={etablissementNom}
            />
          ) : (
            <Header
              onLogout={handleLogout}
              isSigningOut={isSigningOut}
              onNavigateToProfile={() => navigate("/profil")}
              userRole={userRole}
              userName={userName}
              etablissementNom={etablissementNom}
            />
          )}
          <div className="flex flex-1 overflow-hidden">
            {!isMobile && (
              userRole === "admin" ? (
                <SidebarAdmin
                  currentPage={currentPage}
                  onNavigate={handleNavigate}
                />
              ) : (
                <SidebarUser
                  currentPage={currentPage}
                  onNavigate={handleNavigate}
                />
              )
            )}
            <main
              className={cn(
                "flex-1 bg-page relative",
                isMobile
                  ? "min-h-[calc(100dvh-56px-64px-env(safe-area-inset-top,0px)-0.75rem)] overflow-y-auto"
                  : "h-[calc(100vh-56px)] overflow-hidden",
                // Reserve la hauteur de la BottomNav (nav + safe-area) sauf sur les
                // wizards plein ecran ou la nav est masquee.
                isMobile && !isFullscreenForm && "pb-[calc(4.5rem+env(safe-area-inset-bottom))]"
              )}
            >
              <Outlet />
              {!isMobile && userRole === "admin" && (
                <Suspense fallback={null}>
                  <PanneauDemandeClient />
                </Suspense>
              )}
            </main>
          </div>
          {isMobile && !isFullscreenForm && <BottomNav role={userRole} />}
        </div>
      </DemandeClientMemoProvider>
    </NavigationContext.Provider>
  );
}
